import type { WaterLog } from '../types/production'

import { api } from './api'
import { normalizeToIsoZMidnight } from './dateNormalization'
import {
  buildQueuedEntityMutationResult,
  clearEntityBatchSyncQueueBlockers,
  readEntityBatchSyncStatus,
  retryEntityBatchSyncStatus,
  type EntityBatchSyncStatus,
  type EntityMutationResult,
  type EntityMutationSyncState
} from './entityMutationSync'
import { deleteScopedKey, readScopedString, readWaterJSON, writeWaterJSON } from './waterStorage'
import { ensureOfflineReady } from '../offline/initOffline'
import { getOfflineDb } from '../offline/dbFactory'
import { enqueueEntityChange } from '../offline/outbox/entityOutbox'
import { syncEntityOutboxNow } from '../offline/outbox/syncEntityOutboxNow'
import { dexieActivityRepo } from '../offline/repos/DexieActivityRepo'

export type WaterDataRow = {
  id: string
  date: string
  lot: string
  // Charter D-2: WaterLog readings belong to the production unit (pond), not a
  // lot. `lotId` is kept on the row only as client-side context for the Water
  // screen's lot picker and the report datasets' lot scoping — it MUST NEVER be
  // sent in the sync payload. The server rejects any WaterLog payload that
  // carries a `lotId` key (WaterLogService.assertNoLotId → 400), which is a
  // non-retryable error that stalls the whole sync outbox. See toWaterLogPayload
  // and syncUpsertWaterLog, both of which drop it.
  lotId?: string
  unmappedLot?: string
  temperature: number | null
  ph: number | null
  dissolvedOxygen: number | null
  dissolvedOxygenSaturation: number | null
  manualDissolvedOxygen: number | null
  manualDissolvedOxygenSaturation: number | null
  salinity: number | null
  notes: string
  metadata?: WaterLogMetadata
  // Crew Mode Charter D-4: the operator identity active at the moment of
  // capture, stamped client-side (see src/utils/operatorSession.ts). The
  // existing admin Water.tsx write path never sets this — it stays
  // undefined there, which JSON.stringify drops from the outbox payload,
  // so the server's principal-fallback in captureAttribution.ts applies
  // unchanged for admin-app writes.
  capturedByActorId?: string
  /** 'crewMode' when the reading was captured in Crew Mode (server-set,
   *  findings #55). Display only; never sent back to the server. */
  captureSource?: string
}

export type WaterLogSourceType = 'manual' | 'csv' | 'logger' | 'device'

export type WaterLogMetadata = Record<string, unknown> & {
  sourceType?: WaterLogSourceType
  batchId?: string
  vendor?: string
  externalSourceId?: string
}

type WaterTableContext = {
  farmId: string
  productionUnitId: string
  farmName?: string
  productionUnitName?: string
}

export type WaterTableRowMutationResult = EntityMutationResult<WaterDataRow>
export type WaterTableRowDeleteResult = EntityMutationResult<void>

const isOnline = (): boolean => {
  if (typeof navigator === 'undefined') return false
  return navigator.onLine
}

const canonicalKey = (context: WaterTableContext): string => (
  `water-v2-${context.farmId}-${context.productionUnitId}`
)

const canonicalMigrationKey = (context: WaterTableContext): string => (
  `water-migrated-v2-${context.farmId}-${context.productionUnitId}`
)

const legacyKeys = (context: WaterTableContext): string[] => {
  const farmName = (context.farmName ?? '').trim()
  const unitName = (context.productionUnitName ?? '').trim()
  if (!farmName || !unitName) return []
  return [
    `water-${farmName}-${unitName}`,
    `water-${farmName} - ${unitName}`
  ]
}

const legacyMigrationKeys = (context: WaterTableContext): string[] => {
  const farmName = (context.farmName ?? '').trim()
  const unitName = (context.productionUnitName ?? '').trim()
  if (!farmName || !unitName) return []
  return [
    `water-migrated-${farmName}-${unitName}`,
    `water-migrated-${farmName} - ${unitName}`
  ]
}

const migrateExistingDates = (rows: WaterDataRow[]): WaterDataRow[] => {
  let migrated = false

  const next = rows.map((row) => {
    const originalDate = (row.date ?? '').trim()
    if (!originalDate) return row

    const normalized = normalizeToIsoZMidnight(originalDate)
    if (!normalized.ok) return row
    if (normalized.isoZ === originalDate) return row

    migrated = true
    return { ...row, date: normalized.isoZ }
  })

  return migrated ? next : rows
}

const parseOptionalNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) return null
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const normalizeWaterMetadata = (value: unknown): WaterLogMetadata | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined

  const record = { ...(value as Record<string, unknown>) }
  const sourceType = typeof record.sourceType === 'string' ? record.sourceType.trim().toLowerCase() : ''
  const batchId = typeof record.batchId === 'string' ? record.batchId.trim() : ''
  const vendor = typeof record.vendor === 'string' ? record.vendor.trim() : ''
  const externalSourceId = typeof record.externalSourceId === 'string' ? record.externalSourceId.trim() : ''

  if (sourceType === 'manual' || sourceType === 'csv' || sourceType === 'logger' || sourceType === 'device') {
    record.sourceType = sourceType
  } else {
    delete record.sourceType
  }

  if (batchId) {
    record.batchId = batchId
  } else {
    delete record.batchId
  }

  if (vendor) {
    record.vendor = vendor
  } else {
    delete record.vendor
  }

  if (externalSourceId) {
    record.externalSourceId = externalSourceId
  } else {
    delete record.externalSourceId
  }

  return Object.keys(record).length > 0 ? record : undefined
}

const toRowFromApi = (log: WaterLog): WaterDataRow => ({
  id: log.id,
  date: log.recordedAt,
  lotId: log.lotId,
  lot: log.lot ?? '',
  // Charter D-4: keep the operator identity on the row when a Crew Mode
  // reading is re-hydrated from the server (storage cleared, second device).
  // Without this the row drops out of the operator "today" list, which
  // filters on capturedByActorId — unlike feed/mortality/weight, which
  // already carry it through their list APIs.
  ...(log.capturedByActorId ? { capturedByActorId: log.capturedByActorId } : {}),
  ...(log.captureSource ? { captureSource: log.captureSource } : {}),
  temperature: parseOptionalNumber(log.temperatureC),
  ph: parseOptionalNumber(log.ph),
  dissolvedOxygen: parseOptionalNumber(log.dissolvedOxygenMgL),
  dissolvedOxygenSaturation: parseOptionalNumber(log.dissolvedOxygenSaturationPct),
  manualDissolvedOxygen: parseOptionalNumber(log.manualDissolvedOxygenMgL),
  manualDissolvedOxygenSaturation: parseOptionalNumber(log.manualDissolvedOxygenSaturationPct),
  salinity: parseOptionalNumber(log.salinityPpt),
  notes: log.notes ?? '',
  metadata: normalizeWaterMetadata(log.metadata)
})

// Server-Side Flag Enforcement v0 (docs/crew-mode/prd/crew-mode-server-side-
// flag-enforcement-v0.md): `source` is set only by Crew Mode's capture
// screen, threaded through from upsertWaterTableRowAndSync. Never persisted
// locally — it exists only in the outbox payload for the server's
// WaterLogService.create guard to read. The admin Water.tsx path never sets
// this (shared functions here default it to absent).
type WaterLogSourceOptions = { source?: 'crewMode' }

const toWaterLogPayload = (
  context: WaterTableContext,
  row: WaterDataRow,
  options?: WaterLogSourceOptions
): Record<string, unknown> => ({
  id: row.id,
  farmId: context.farmId,
  productionUnitId: context.productionUnitId,
  // Charter D-2: `lotId` is deliberately omitted. WaterLog is production-unit
  // scoped; the server rejects any payload with a `lotId` key. Lot context
  // travels as the `lot` label only.
  lot: row.lot || undefined,
  recordedAt: row.date,
  temperatureC: row.temperature !== null && row.temperature !== undefined ? String(row.temperature) : undefined,
  ph: row.ph !== null && row.ph !== undefined ? String(row.ph) : undefined,
  dissolvedOxygenMgL: row.dissolvedOxygen !== null && row.dissolvedOxygen !== undefined ? String(row.dissolvedOxygen) : undefined,
  dissolvedOxygenSaturationPct: row.dissolvedOxygenSaturation !== null && row.dissolvedOxygenSaturation !== undefined ? String(row.dissolvedOxygenSaturation) : undefined,
  manualDissolvedOxygenMgL: row.manualDissolvedOxygen !== null && row.manualDissolvedOxygen !== undefined ? String(row.manualDissolvedOxygen) : undefined,
  manualDissolvedOxygenSaturationPct: row.manualDissolvedOxygenSaturation !== null && row.manualDissolvedOxygenSaturation !== undefined ? String(row.manualDissolvedOxygenSaturation) : undefined,
  salinityPpt: row.salinity !== null && row.salinity !== undefined ? String(row.salinity) : undefined,
  notes: row.notes || undefined,
  metadata: normalizeWaterMetadata(row.metadata),
  capturedByActorId: row.capturedByActorId || undefined,
  ...(options?.source ? { source: options.source } : {})
})

export const loadWaterTableRows = (context: WaterTableContext): WaterDataRow[] => {
  const primaryKey = canonicalKey(context)
  const primaryExists = readScopedString(primaryKey) !== null

  let rows: WaterDataRow[] = []
  if (primaryExists) {
    rows = readWaterJSON<WaterDataRow[]>(primaryKey, [])
  } else {
    const candidates = legacyKeys(context)
    for (const legacyKey of candidates) {
      if (readScopedString(legacyKey) === null) continue
      rows = readWaterJSON<WaterDataRow[]>(legacyKey, [])
      break
    }

    if (rows.length > 0) {
      // Backfill canonical key so future loads are stable by IDs.
      writeWaterJSON(primaryKey, rows)
    }
  }

  const migratedFlagExists = readScopedString(canonicalMigrationKey(context)) !== null
    || legacyMigrationKeys(context).some((key) => readScopedString(key) !== null)

  if (!migratedFlagExists && rows.length > 0) {
    const migratedRows = migrateExistingDates(rows)
    if (migratedRows !== rows) {
      // Keep a scoped backup.
      writeWaterJSON(`water-backup-${Date.now()}`, rows)
      rows = migratedRows
      writeWaterJSON(primaryKey, rows)
      // Also keep legacy mirrors in sync until other screens migrate.
      legacyKeys(context).forEach((key) => writeWaterJSON(key, rows))
    }
    // Set migration flags (canonical + legacy) to avoid re-running.
    writeWaterJSON(canonicalMigrationKey(context), new Date().toISOString())
    legacyMigrationKeys(context).forEach((key) => writeWaterJSON(key, new Date().toISOString()))
  }

  return rows
}

type WaterTableSaveOptions = {
  skipSync?: boolean
}

type WaterTableEnqueueOptions = WaterLogSourceOptions & {
  skipAutoSync?: boolean
}

export const enqueueWaterRowsUpsert = async (
  context: WaterTableContext,
  rows: WaterDataRow[],
  options?: WaterTableEnqueueOptions
): Promise<void> => {
  if (rows.length === 0) return

  void dexieActivityRepo.log({
    source: 'outbox',
    level: 'info',
    title: 'Water logs updated',
    detail: `Queued ${rows.length} row${rows.length === 1 ? '' : 's'} for sync`,
    farmId: context.farmId,
    productionUnitId: context.productionUnitId,
    dedupeKey: `waterLog:upsert:${context.farmId}:${context.productionUnitId}`,
    incrementCount: rows.length,
    dedupeWindowMs: 45_000
  }).catch(() => {})
  for (const row of rows) {
    await enqueueEntityChange({
      entityType: 'waterLog',
      op: 'upsert',
      entityId: row.id,
      payload: toWaterLogPayload(context, row, { source: options?.source })
    })
  }
  if (!options?.skipAutoSync && typeof navigator !== 'undefined' && navigator.onLine) {
    void (syncEntityOutboxNow({ maxEntries: 50 }).catch(() => {}))
  }
}

export const upsertWaterTableRowAndSync = async (
  context: WaterTableContext,
  rows: WaterDataRow[],
  row: WaterDataRow,
  options?: WaterLogSourceOptions
): Promise<WaterTableRowMutationResult> => {
  // Persist the row we are about to sync. Callers are expected to pass the
  // full post-insert array, but merge `row` in defensively so a caller that
  // passes the pre-insert array (as the Crew Mode water capture did) still
  // lands the reading in local storage — otherwise `loadWaterTableRows`, and
  // anything reading through it (e.g. the operator "today" list), never sees
  // it. Idempotent for callers that already include `row`.
  const merged = rows.some((existing) => existing.id === row.id)
    ? rows.map((existing) => (existing.id === row.id ? row : existing))
    : [...rows, row]

  saveWaterTableRows(context, merged, { skipSync: true })
  await ensureOfflineReady()
  await enqueueWaterRowsUpsert(context, [row], { skipAutoSync: true, source: options?.source })

  return buildQueuedEntityMutationResult(row, {
    entityType: 'waterLog',
    entityId: row.id,
    maxEntries: 50,
    maxPasses: 5
  })
}

export const saveWaterTableRows = (
  context: WaterTableContext,
  rows: WaterDataRow[],
  options?: WaterTableSaveOptions
): void => {
  const primaryKey = canonicalKey(context)
  writeWaterJSON(primaryKey, rows)
  legacyKeys(context).forEach((key) => writeWaterJSON(key, rows))

  if (options?.skipSync) return

  // Fire-and-forget: enqueue each row as an upsert so the API stays in sync.
  void (enqueueWaterRowsUpsert(context, rows).catch(() => {}))
}

export const deleteWaterTableRows = (context: WaterTableContext): void => {
  deleteScopedKey(canonicalKey(context))
  deleteScopedKey(canonicalMigrationKey(context))
  legacyKeys(context).forEach((key) => deleteScopedKey(key))
  legacyMigrationKeys(context).forEach((key) => deleteScopedKey(key))
}

// Call this when a single water-log row is deleted from the UI so the API is notified.
export const dequeueWaterRow = async (
  id: string,
  options: WaterTableEnqueueOptions & { reason: string; operatorActorId?: string }
): Promise<void> => {
  void dexieActivityRepo.log({
    source: 'outbox',
    level: 'warning',
    title: 'Water log deleted',
    detail: 'Sync queued',
    dedupeKey: `waterLog:delete:${id}`,
    dedupeWindowMs: 10_000
  }).catch(() => {})

  await enqueueEntityChange({
    entityType: 'waterLog',
    op: 'delete',
    entityId: id,
    reason: options.reason,
    operatorActorId: options.operatorActorId
  })
  if (!options.skipAutoSync && typeof navigator !== 'undefined' && navigator.onLine) {
    void (syncEntityOutboxNow({ maxEntries: 10 }).catch(() => {}))
  }
}

export const deleteWaterTableRowAndSync = async (
  context: WaterTableContext,
  rows: WaterDataRow[],
  rowId: string,
  audit: { reason: string; operatorActorId?: string }
): Promise<WaterTableRowDeleteResult> => {
  saveWaterTableRows(context, rows, { skipSync: true })
  await ensureOfflineReady()
  await dequeueWaterRow(rowId, { skipAutoSync: true, reason: audit.reason, operatorActorId: audit.operatorActorId })

  return buildQueuedEntityMutationResult(undefined, {
    entityType: 'waterLog',
    entityId: rowId,
    maxEntries: 50,
    maxPasses: 5
  })
}

export const readWaterTableRowSyncState = async (rowIds: string[]): Promise<EntityMutationSyncState> => {
  const status = await readWaterTableRowSyncStatus(rowIds)
  return status.syncState
}

export const readWaterTableRowSyncStatus = async (rowIds: string[]): Promise<EntityBatchSyncStatus> => {
  return readEntityBatchSyncStatus('waterLog', rowIds)
}

export const retryWaterTableRowSync = async (rowIds: string[]): Promise<EntityMutationSyncState> => {
  const status = await retryWaterTableRowSyncStatus(rowIds)
  return status.syncState
}

export const retryWaterTableRowSyncStatus = async (rowIds: string[]): Promise<EntityBatchSyncStatus> => {
  return retryEntityBatchSyncStatus({
    entityType: 'waterLog',
    entityIds: rowIds,
    maxEntries: 50,
    maxPasses: 5
  })
}

export const clearWaterTableRowSyncQueueBlockers = async (rowIds: string[]): Promise<EntityBatchSyncStatus> => {
  return clearEntityBatchSyncQueueBlockers({
    entityType: 'waterLog',
    entityIds: rowIds,
    maxEntries: 50,
    maxPasses: 5
  })
}

export const refreshWaterTableRowsFromApi = async (context: WaterTableContext): Promise<WaterDataRow[]> => {
  const logs = await api.waterLogs.list({
    farmId: context.farmId,
    productionUnitId: context.productionUnitId
  })

  const remoteRows = Array.isArray(logs) ? logs.map(toRowFromApi) : []
  const local = loadWaterTableRows(context)

  if (remoteRows.length === 0) return local

  // Findings #86 (rehearsal 2026-09-29): the server's copy wins for every row
  // it has — a "Corregir…" is a direct server write, and keeping the old local
  // copy left the table and every report built on this device stale. A row
  // with a change still waiting in this device's outbox keeps its local
  // version (it is newer), and rows the server does not know yet stay.
  const merged = mergeWaterRowsFromServer(local, remoteRows, await readPendingWaterRowIds())
  saveWaterTableRows(context, merged, { skipSync: true })
  return merged
}

/**
 * Findings #217: every unit's readings of a facility since `fromRecordedAt` in
 * one request, merged per unit like `refreshWaterTableRowsFromApi`, so a screen
 * that reads several units (or a unit's measuring point) sees readings taken
 * on other devices.
 */
export const refreshFarmWaterRowsFromApi = async (args: {
  farmId: string
  farmName?: string
  units: ReadonlyArray<{ id: string; name?: string | null }>
  fromRecordedAt?: string
}): Promise<void> => {
  const logs = await api.waterLogs.list({ farmId: args.farmId, fromRecordedAt: args.fromRecordedAt })
  if (!Array.isArray(logs) || logs.length === 0) return
  const byUnit = new Map<string, WaterDataRow[]>()
  for (const log of logs) {
    const unitRows = byUnit.get(log.productionUnitId) ?? []
    unitRows.push(toRowFromApi(log))
    byUnit.set(log.productionUnitId, unitRows)
  }
  const pendingIds = await readPendingWaterRowIds()
  for (const unit of args.units) {
    const remoteRows = byUnit.get(unit.id)
    if (!remoteRows) continue
    const context = { farmId: args.farmId, productionUnitId: unit.id, farmName: args.farmName, productionUnitName: unit.name ?? undefined }
    saveWaterTableRows(context, mergeWaterRowsFromServer(loadWaterTableRows(context), remoteRows, pendingIds), { skipSync: true })
  }
}

/** Pure merge for `refreshWaterTableRowsFromApi` (findings #86). */
export const mergeWaterRowsFromServer = (
  local: WaterDataRow[],
  remoteRows: WaterDataRow[],
  pendingIds: Set<string>
): WaterDataRow[] => {
  const remoteById = new Map(remoteRows.map((row) => [row.id, row]))
  const merged = local.map((row) => {
    const remote = remoteById.get(row.id)
    if (!remote || pendingIds.has(row.id)) return row
    // Client-only context the server never stores (lot picker state).
    return { ...remote, lotId: remote.lotId ?? row.lotId, unmappedLot: row.unmappedLot }
  })
  const localIds = new Set(local.map((row) => row.id))
  for (const row of remoteRows) {
    if (!localIds.has(row.id)) merged.push(row)
  }
  return merged
}

const readPendingWaterRowIds = async (): Promise<Set<string>> => {
  try {
    const entries = await getOfflineDb().entityOutbox.filter((entry) => entry.entityType === 'waterLog').toArray()
    return new Set(entries.map((entry) => entry.entityId))
  } catch {
    return new Set()
  }
}

export const getWaterTableCanonicalKey = (context: WaterTableContext): string => canonicalKey(context)